"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PageCareerSchema } from "@/sanity/pages/PageCareerTemplate";
import { cn } from "@/lib/utils";
import Image from "next/image";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";

export default function TradersSection({
  data,
  className,
}: {
  data: PageCareerSchema;
  className?: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const isBrazil = data?.settings?.slug?.current?.endsWith("/br");

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <section
      className={cn(
        "relative overflow-clip bg-white lg:border-y w-full",
        className,
      )}
    >
      {data?.profileImages && (
        <>
          {/* Desktop */}
          <div className="hidden lg:block relative w-full h-full">
            <Carousel
              className="w-full"
              setApi={setApi}
              opts={{
                align: "center",
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 2000,
                  stopOnInteraction: false,
                }),
              ]}
            >
              <CarouselContent className="">
                {data?.profileImages.map((item, index) => (
                  <CarouselItem key={index} className="basis-[47.5rem]">
                    <motion.div
                      className={cn(
                        "relative rounded-[2rem] overflow-clip h-fit transition-transform",
                        current === index ? "scale-100" : "scale-95",
                      )}
                    >
                      <Image
                        className="relative w-full h-full z-[1]"
                        src={item.image}
                        quality={100}
                        alt=""
                      />
                      <div className="absolute z-[2] flex flex-col pl-[5rem] justify-center bottom-0 h-[30%] w-full bg-gradient-to-b from-black/0 to-black/80">
                        {item?.text && (
                          <div className="richtext richtext-start prose-h3:text-[2.5rem] prose-h3:font-[600] prose-h3:tracking-[-.6px] prose-h3:leading-[112%] prose-h3:m-0 prose-h3:text-white prose-p:m-0 prose-p:text-white/80 prose-p:text-[1.75rem] prose-p:font-[400]">
                            <PortableText
                              value={item?.text}
                              components={richTextComponent}
                            />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>

          {/* Mobile */}
          <Carousel
            className="w-full lg:hidden"
            setApi={setApi}
            opts={{
              align: "center",
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 2000,
                stopOnInteraction: false,
              }),
            ]}
          >
            <CarouselContent className="-ml-[1rem]">
              {data?.profileImages.map((item, index) => (
                <CarouselItem key={index} className="pl-[1rem] basis-auto">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.08,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                    className="overflow-hidden w-[27.4rem] h-[27.4rem] rounded-[2rem]"
                  >
                    {item?.image && (
                      <Image
                        className="w-full h-full object-cover object-[center_20%]"
                        src={item.image}
                        quality={90}
                        alt=""
                        width={274}
                        height={274}
                      />
                    )}
                  </motion.div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </>
      )}
    </section>
  );
}
